import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * GIWAHS — Loading / Empty / Error states
 * ---------------------------------------------------------------------
 * Lightweight, presentational components. Designed to slot anywhere the
 * data hooks return loading/empty/error without redesigning the page.
 */

export const LoadingState = ({ label = 'Loading…', minHeight = 'min-h-[320px]' }) => (
  <div
    data-testid="loading-state"
    className={`flex w-full flex-col items-center justify-center ${minHeight} border border-neutral-200 bg-[#FAFAF7]`}
  >
    <Loader2 size={20} strokeWidth={1.5} className="animate-spin text-neutral-600" />
    <span className="mt-3 label-overline text-neutral-500">{label}</span>
  </div>
);

export const EmptyState = ({ title = 'No results', description, action, minHeight = 'min-h-[280px]' }) => (
  <div
    data-testid="empty-state"
    className={`flex w-full flex-col items-center justify-center border border-dashed border-neutral-300 bg-white px-6 py-12 text-center ${minHeight}`}
  >
    <div className="label-overline text-neutral-500">Empty</div>
    <h3 className="mt-3 font-display text-xl tracking-tight md:text-2xl" style={{ fontWeight: 600 }}>{title}</h3>
    {description && <p className="mt-2 max-w-md text-sm leading-relaxed text-neutral-600">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const ErrorState = ({ title = 'Something went wrong', description, onRetry, minHeight = 'min-h-[280px]' }) => (
  <div
    data-testid="error-state"
    className={`flex w-full flex-col items-center justify-center border border-red-200 bg-red-50/40 px-6 py-12 text-center ${minHeight}`}
  >
    <div className="label-overline text-red-700">Error</div>
    <h3 className="mt-3 font-display text-xl tracking-tight md:text-2xl text-red-900" style={{ fontWeight: 600 }}>{title}</h3>
    {description && <p className="mt-2 max-w-md text-sm leading-relaxed text-red-800/80">{description}</p>}
    {onRetry && (
      <button
        type="button"
        data-testid="error-retry"
        onClick={onRetry}
        className="mt-5 inline-flex items-center gap-2 border border-red-700 px-4 py-2 label-overline text-red-700 hover:bg-red-700 hover:text-white"
      >
        Try again
      </button>
    )}
  </div>
);

/**
 * AsyncBoundary — convenience wrapper that picks the right state
 * based on the standard hook return shape. Use inside a render path
 * when the page only renders content after data resolves.
 *
 *   const { data, loading, error, refetch } = useSuppliers();
 *   return (
 *     <AsyncBoundary loading={loading} error={error} empty={!data?.length} refetch={refetch}>
 *       <Grid items={data} />
 *     </AsyncBoundary>
 *   );
 */
export const AsyncBoundary = ({ loading, error, empty, refetch, emptyTitle, emptyDescription, children }) => {
  if (loading) return <LoadingState />;
  if (error)   return <ErrorState description={error?.message} onRetry={refetch} />;
  if (empty)   return <EmptyState title={emptyTitle} description={emptyDescription} />;
  return children;
};
